import React, { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { useLang } from '@/i18n/LangContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_innovate-ghana-tech/artifacts/0dmqxsnu_0e0a97331ac163b7d8d3f4fa458da7ea9f7b5c35ae763d1673b82962b564a212.png";

export default function Header() {
  const { t, lang, setLang, LANGS } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const navLinks = [
    { label: t.nav.home, href: '#accueil' },
    { label: t.nav.about, href: '#apropos' },
    { label: t.nav.services, href: '#services' },
    { label: t.nav.products, href: '#produits' },
    { label: t.nav.why, href: '#pourquoi' },
    { label: t.nav.blog, href: '#blog' },
    { label: t.nav.contact, href: '#contact' },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header data-testid="main-header" className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${scrolled ? 'backdrop-blur-xl bg-[#0B132B]/90 border-b border-white/10 shadow-lg' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
        <a href="#accueil" onClick={() => handleNav('#accueil')} className="flex items-center gap-3 group" data-testid="header-logo-link">
          <img src={LOGO_URL} alt="GIT NFT GHANA" className="h-12 w-auto transition-transform group-hover:scale-105" />
        </a>

        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <button key={link.href} onClick={() => handleNav(link.href)} data-testid={`nav-${link.href.replace('#', '')}`} className="px-3 py-2 text-sm font-medium text-white/80 hover:text-[#00D186] transition-colors duration-300 tracking-wide">
              {link.label}
            </button>
          ))}

          {/* Language Switcher */}
          <div className="relative ml-2">
            <button onClick={() => setLangOpen(!langOpen)} data-testid="lang-switcher" className="flex items-center gap-1 px-3 py-2 text-sm text-white/70 hover:text-white transition-colors">
              <Globe size={14} />
              <span className="font-bold">{lang.toUpperCase()}</span>
            </button>
            {langOpen && (
              <div className="absolute top-full right-0 mt-1 bg-[#111C3A] border border-white/10 rounded-lg shadow-xl py-1 min-w-[100px]" data-testid="lang-dropdown">
                {LANGS.map((l) => (
                  <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }} data-testid={`lang-${l.code}`} className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 transition-colors ${lang === l.code ? 'text-[#00D186] font-bold' : 'text-white/70'}`}>
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button onClick={() => handleNav('#contact')} data-testid="nav-cta-contact" className="ml-3 px-6 py-2.5 bg-[#00D186] text-[#0B132B] text-sm font-bold rounded-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#00D186]/20">
            {t.nav.cta}
          </button>
        </nav>

        <button className="lg:hidden text-white p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileOpen && (
        <nav className="lg:hidden bg-[#0B132B]/98 backdrop-blur-xl border-t border-white/10 animate-fade-in" data-testid="mobile-nav">
          <div className="px-6 py-6 flex flex-col gap-2">
            {navLinks.map((link) => (
              <button key={link.href} onClick={() => handleNav(link.href)} className="text-left px-4 py-3 text-white/80 hover:text-[#00D186] font-medium transition-colors border-b border-white/5 last:border-0">
                {link.label}
              </button>
            ))}
            <div className="flex gap-2 mt-3 px-4">
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => { setLang(l.code); setMobileOpen(false); }} className={`px-3 py-1.5 text-xs font-bold rounded-sm ${lang === l.code ? 'bg-[#00D186] text-[#0B132B]' : 'bg-white/5 text-white/60'}`}>
                  {l.label}
                </button>
              ))}
            </div>
            <button onClick={() => handleNav('#contact')} className="mt-4 px-6 py-3 bg-[#00D186] text-[#0B132B] font-bold rounded-sm text-center">
              {t.nav.cta}
            </button>
          </div>
        </nav>
      )}
    </header>
  );
}
